/// <reference types="vite/client" />
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const LogoIcon = ({ className = "h-12 w-12" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M50 2L91.6 26V74L50 98L8.4 74V26L50 2Z" fill="#136BCE" />
    <path d="M50 22L74 36L50 50L26 36Z" stroke="white" strokeWidth="8" strokeLinejoin="round" />
    <path d="M26 36V70L50 84V50" stroke="white" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M74 36V70" stroke="white" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

type Step = 'form' | 'success';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<Step>('form');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
      const response = await fetch(`${baseUrl}/api/v1/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to send reset link. Please try again.');
      }

      setStep('success');
    } catch (err: any) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-white lg:grid lg:grid-cols-2 lg:bg-slate-900">
      {/* Sidebar - Hidden on mobile */}
      <div className="relative hidden h-full w-full lg:flex lg:flex-col lg:justify-end bg-slate-900 overflow-hidden">
        <img
          alt="Fabric specialist"
          className="absolute inset-0 h-full w-full object-cover"
          src="https://lh3.googleusercontent.com/aida-public/AB6AXuC5W_0gA08gcjaVFyo65-4lgfwfIvqG0Sl-L6XUQE_R2-0KA4A1z3rn0YyEpRRWGHJbA8zuTBLwp13wN0Vf1saWQM9roQpBore6MEbby8ohiJXm8-XjAnp-itVNPd6l3UurtqbKdAP3x_ozMJH7kcNFRed1_rZEBGrigOgcJpdZfPXNgoeoa0iQMe8IokZ47bL4I-vcadQJtyb44z1BhDEEyor6benuC94F63dimUkHfJdVIVEJHAYcEwMbHTze3AQyoLBkkzmP7CB7"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#082042] via-[#136bce]/60 to-slate-900/30 mix-blend-multiply opacity-95"></div>
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/40 via-transparent to-transparent"></div>
        <div className="relative z-10 p-12 pb-24 xl:p-20 xl:pb-32 text-white">
          <h1 className="text-5xl xl:text-6xl font-extrabold tracking-tight mb-6 drop-shadow-lg leading-tight">
            Textile Quality <br />Reimagined
          </h1>
          <p className="text-2xl xl:text-3xl font-medium text-blue-50/90 drop-shadow-md border-l-4 border-[#136bce] pl-6">
            Ensuring excellence in every thread
          </p>
        </div>
      </div>

      {/* Form Container */}
      <div className="flex min-h-[100dvh] w-full flex-col justify-between bg-white px-6 py-12 lg:px-20 xl:px-32 shadow-2xl lg:shadow-none z-20">
        <div className="flex-1 flex flex-col justify-center">
          <div className="w-full max-w-[460px] mx-auto">
            {/* Logo */}
            <div className="mb-12 flex flex-col items-start gap-6">
              <div className="flex items-center gap-5">
                <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-white text-white shadow-xl shadow-blue-100 ring-4 ring-blue-50">
                  <LogoIcon className="h-14 w-14" />
                </div>
                <span className="text-5xl font-extrabold tracking-tight text-[#136BCE] uppercase">QA MATE</span>
              </div>
            </div>

            {step === 'form' ? (
              <>
                {/* Heading */}
                <div className="mb-10 text-left">
                  <h2 className="text-3xl font-bold tracking-tight text-slate-900">Forgot Password?</h2>
                  <p className="mt-3 text-base text-slate-500 leading-relaxed">
                    Enter your registered email address and we'll send you a link to reset your password.
                  </p>
                </div>

                {/* Form */}
                <form className="space-y-6" onSubmit={handleSubmit}>
                  {error && (
                    <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700 border border-red-200">
                      {error}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email address</label>
                    <div className="relative group">
                      <span className="material-symbols-outlined pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[22px] group-focus-within:text-[#136bce] transition-colors">mail</span>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="block w-full rounded-lg border-slate-300 bg-slate-50 py-3.5 pl-11 pr-3 text-slate-900 placeholder-slate-400 focus:border-[#136bce] focus:ring-[#136bce] focus:bg-white sm:text-sm shadow-sm transition-all duration-200"
                        placeholder="professional@example.com"
                        required
                      />
                    </div>
                  </div>

                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="flex w-full items-center justify-center rounded-lg border border-transparent bg-[#136bce] py-3.5 px-4 text-sm font-bold text-white shadow-md hover:bg-[#0e52a0] hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#136bce] focus:ring-offset-2 transition-all duration-200 transform active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {isLoading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sending link...
                        </>
                      ) : (
                        'Send Reset Link'
                      )}
                    </button>
                  </div>
                </form>

                <div className="mt-8 text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#136bce] hover:text-[#0e52a0] transition-colors"
                  >
                    <span className="material-symbols-outlined text-[18px]">arrow_back</span>
                    Back to Sign In
                  </Link>
                </div>
              </>
            ) : (
              /* Success State */
              <>
                <div className="mb-10 text-left">
                  <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-green-50 ring-4 ring-green-100">
                    <span className="material-symbols-outlined text-green-600 text-[32px]">mark_email_read</span>
                  </div>
                  <h2 className="text-3xl font-bold tracking-tight text-slate-900">Check Your Email</h2>
                  <p className="mt-3 text-base text-slate-500 leading-relaxed">
                    We've sent a password reset link to{' '}
                    <span className="font-semibold text-slate-700">{email}</span>.
                    Please check your inbox and follow the instructions.
                  </p>
                </div>

                <div className="rounded-lg bg-blue-50 border border-blue-100 p-4 text-sm text-blue-700">
                  <div className="flex items-start gap-3">
                    <span className="material-symbols-outlined text-blue-500 text-[20px] mt-0.5 shrink-0">info</span>
                    <p>
                      Didn't receive it? Check your spam folder or{' '}
                      <button
                        onClick={() => { setStep('form'); setError(null); }}
                        className="font-semibold underline underline-offset-2 hover:text-blue-900 transition-colors"
                      >
                        try again
                      </button>
                      .
                    </p>
                  </div>
                </div>

                <div className="mt-8 text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#136bce] hover:text-[#0e52a0] transition-colors"
                  >
                    <span className="material-symbols-outlined text-[18px]">arrow_back</span>
                    Back to Sign In
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="w-full max-w-[460px] mx-auto pt-10 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
            © {new Date().getFullYear()} QA MATE Systems
          </p>
          <nav className="flex items-center gap-4 text-[10px] font-bold text-slate-500 uppercase tracking-wide">
            <Link to="/privacy-policy" className="hover:text-[#136bce] transition-colors">Privacy Policy</Link>
            <span className="h-1 w-1 rounded-full bg-slate-300"></span>
            <Link to="/terms-conditions" className="hover:text-[#136bce] transition-colors">Terms & Conditions</Link>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
